import { XMarkIcon } from "@heroicons/react/20/solid";
import { Reminder, reminderHref } from "@sp-crm/core";
import { Checkbox } from "components/ui/checkbox";
import { Transition } from "lib/headlessui/components/transition/transition";
import moment from "moment";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useFeaturesQuery } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { useReminderContext } from "../../contexts/reminder";

export const NgReminders: React.FunctionComponent = () => {
    const { isReminderVisible, reminders, setVisible, completeTask } =
        useReminderContext();

    if (isReminderVisible) {
        return (
            <NgRemindersPopup
                reminders={reminders}
                setVisible={setVisible}
                completeTask={completeTask}
            />
        );
    }

    return null;
};

export const NgRemindersPopup: React.FC<{
    reminders: Reminder[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    setVisible: (arg: boolean) => any;
    completeTask: (reminder: Reminder) => void;
}> = ({ reminders, setVisible, completeTask }) => {
    const [backdropOpacity, setBackdropOpacity] = useState(0.2); // Start darker
    const panelRef = useRef<HTMLDivElement>(null);
    const [isClosing, setIsClosing] = useState(false);
    const [closeTransform, setCloseTransform] = useState<string | undefined>(undefined);

    useEffect(() => {
        // After component mounts, fade completely away after a brief moment
        const timer = setTimeout(() => {
            setBackdropOpacity(0);
        }, 300);

        return () => clearTimeout(timer);
    }, []);

    const handleClose = useCallback(() => {
        if (isClosing) {
            return;
        }
        const panel = panelRef.current;
        const bell = document.querySelector<HTMLElement>("[data-reminder-bell]");
        if (panel && bell) {
            const p = panel.getBoundingClientRect();
            const b = bell.getBoundingClientRect();
            const dx = b.left + b.width / 2 - (p.left + p.width / 2);
            const dy = b.top + b.height / 2 - (p.top + p.height / 2);
            setCloseTransform(`translate(${dx}px, ${dy}px) scale(0.05)`);
        }
        setIsClosing(true);
        setTimeout(() => setVisible(false), 300);
    }, [isClosing, setVisible]);

    return (
        <>
            <div
                className="fixed inset-0 bg-black transition-opacity duration-1000 ease-out pointer-events-none z-40"
                style={{ opacity: backdropOpacity }}
            />

            <div className="fixed inset-0 pointer-events-none z-50">
                <div className="flex min-h-full items-start justify-end p-4">
                    <Transition
                        show={true}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95 translate-x-4"
                        enterTo="opacity-100 scale-100 translate-x-0">
                        <div
                            ref={panelRef}
                            style={{
                                transform: closeTransform,
                                opacity: isClosing ? 0 : undefined,
                                transformOrigin: "center",
                            }}
                            className="w-96 max-w-[calc(100vw-2rem)] transform overflow-hidden rounded-lg bg-white border border-gray-200 shadow-xl transition-all duration-[250ms] ease-in pointer-events-auto">
                            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
                                <h2 className="text-base font-medium text-gray-900">
                                    Reminders ({reminders.length})
                                </h2>
                                <button
                                    onClick={handleClose}
                                    className="text-gray-400 hover:text-gray-600">
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="max-h-96 overflow-y-auto">
                                <NgRemindersPopupContent
                                    reminders={reminders}
                                    completeTask={completeTask}
                                />
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </>
    );
};

const NgReminderItem: React.FC<{
    reminder: Reminder;
    completeTask: (reminder: Reminder) => void;
}> = ({ reminder, completeTask }) => {
    const [dismissTimeout, setDismissTimeout] = useState(null);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const features = useFeaturesQuery();
    const href = reminderHref(reminder.parentHref, features.isEnabled);

    const taskDueTime = moment(reminder.when());
    const isDanger = taskDueTime.isBefore(moment().subtract(60, "minute"));

    const onToggleCheckbox = (isChecked: boolean) => {
        // this gives the user 1.5 seconds to uncheck the checkbox if they
        // accidentally clicked the wrong task to dismiss.
        if (isChecked) {
            setDismissTimeout(
                setTimeout(() => {
                    handleEvent("task-complete", { source: "reminders" });
                    setIsAnimatingOut(true);
                    // Give animation time to complete before actually removing
                    setTimeout(() => {
                        completeTask(reminder);
                        setDismissTimeout(null);
                    }, 300);
                }, 1500),
            );
        } else {
            clearTimeout(dismissTimeout);
            setDismissTimeout(null);
        }
    };

    const formatDate = (date: Date) => {
        return moment(date).format("M/D/YY");
    };

    return (
        <div
            className={`flex items-start gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-all duration-300 ease-out ${
                isAnimatingOut
                    ? "opacity-0 -translate-x-4 scale-95"
                    : "opacity-100 translate-x-0 scale-100"
            }`}>
            <div className="flex-shrink-0 mt-0.5">
                <Checkbox
                    onChange={e => onToggleCheckbox(e.target.checked)}
                    checked={dismissTimeout !== null}
                />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 mb-1">
                    {reminder.title}
                </div>
                <Link to={href} className="text-sm text-brand-600 hover:text-brand-800">
                    {reminder.parentName}
                </Link>
            </div>
            <div className="flex-shrink-0 text-right">
                <div className={`text-sm ${isDanger ? "text-red-600" : "text-gray-500"}`}>
                    {formatDate(reminder.when())}
                </div>
            </div>
        </div>
    );
};

export const NgRemindersPopupContent: React.FC<{
    reminders: Reminder[];
    completeTask: (reminder: Reminder) => void;
}> = ({ reminders, completeTask }) => {
    const maxVisibleReminders = 6;
    const sortedReminders = [...reminders].sort((a, b) => {
        return new Date(a.when()).getTime() - new Date(b.when()).getTime();
    });
    const visibleReminders = sortedReminders.slice(-maxVisibleReminders);
    const remainingCount = Math.max(0, sortedReminders.length - maxVisibleReminders);

    if (reminders.length === 0) {
        return <div className="px-4 py-8 text-center text-gray-500">No reminders</div>;
    }

    return (
        <>
            {visibleReminders.map(reminder => (
                <NgReminderItem
                    key={reminder.id}
                    reminder={reminder}
                    completeTask={completeTask}
                />
            ))}
            {remainingCount > 0 && <NgMoreReminders count={remainingCount} />}
        </>
    );
};

const NgMoreReminders: React.FC<{ count: number }> = ({ count }) => {
    return (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-900 mb-1">More ({count})</div>
            <Link to="/tasks" className="text-sm text-gray-600">
                {count} more already due
            </Link>
        </div>
    );
};
